import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import GPSCheckIn from '../components/GPSCheckIn';
import { routesApi, type Route } from '../services/api';
import { getEffectiveAssociateId } from '../utils/associate';
import { useDayTrackingContext } from '../context/DayTrackingContext';
import { useOdometerContext } from '../context/OdometerContext';
import { formatDateToLocal } from '../utils/timesheetRules';
import { cacheRoute, getCachedRoute } from '../utils/scheduleCache';
import { sanitizeDriveData } from '../utils/driveSegment';
// import { useAuth } from '../hooks/useAuth';  // TODO: Re-enable when associate_id is added to User type
import OdometerEntry from '../components/OdometerEntry';
import RetroactiveStartDay, { type RetroactiveStartData } from '../components/RetroactiveStartDay';

function generateIdempotencyKey(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  return `call-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// Info row component
function InfoRow({ label, value, className = '' }: { label: string; value: React.ReactNode; className?: string }) {
  return (
    <div className={`flex items-center justify-between py-2 ${className}`}>
      <span className="text-sm text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{value || '-'}</span>
    </div>
  );
}

// Section component
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
      <h3 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
        {title}
      </h3>
      {children}
    </div>
  );
}

// Progress bar component
function ProgressBar({ completed, total }: { completed: number; total: number }) {
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-600">{completed} of {total} visits</span>
        <span className={`font-semibold ${percentage >= 100 ? 'text-green-600' : 'text-primary-600'}`}>
          {percentage}%
        </span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-slate-200">
        <div
          className={`h-2 rounded-full transition-all ${
            percentage >= 100 ? 'bg-green-500' : percentage >= 50 ? 'bg-primary-500' : 'bg-amber-500'
          }`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
}

export default function CallDetail() {
  const { callId } = useParams<{ callId: string }>();
  const navigate = useNavigate();
  // const { user } = useAuth();  // TODO: Re-enable when associate_id is added to User type
  const [route, setRoute] = useState<Route | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Check-in failures are operational, not fatal: they surface as an
  // inline retryable banner instead of the full-page "Unable to load
  // call" screen (which is reserved for route-load failures). #1991
  const [checkInError, setCheckInError] = useState<string | null>(null);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [showOdometerEntry, setShowOdometerEntry] = useState(false);
  const [pendingGpsOverrideReason, setPendingGpsOverrideReason] = useState<string | undefined>();
  // Retroactive Start Day flow — when a rep arrives on a Call page
  // without having started the day, we MUST collect departure time,
  // start odo, and arrival odo before letting check-in run. Previously
  // CallDetail just called startDay() silently with no data, which is
  // how Rob's row 955198 ended up with DriveOdoStart=0/DriveOdoEnd=1.
  const [showRetroactiveStartDay, setShowRetroactiveStartDay] = useState(false);
  // #2957 — after the retroactive Start-Day backfill persists, continue straight
  // into the check-in (no "tap Check In again" second step / re-prompt loop).
  const [autoCheckInPending, setAutoCheckInPending] = useState(false);
  const [retroactiveDriveData, setRetroactiveDriveData] = useState<{
    startTime: Date;
    endTime: Date;
    durationMinutes: number;
    startOdometer?: number;
    endOdometer?: number;
  } | null>(null);
  // Stable idempotency key for the check-in createCallLog (#1892).
  // Duplicate taps on the same Call within one component lifetime
  // dedupe to the same CallLog row instead of creating duplicates.
  const [checkInKey, setCheckInKey] = useState<string>(() => generateIdempotencyKey());

  // Day tracking context
  const {
    dayData,
    startDay,
    checkIntoStore,
    setCurrentEntryCallLogId,
  } = useDayTrackingContext();

  // Odometer context
  const {
    odometerData,
    startDayOdometer,
    recordStoreArrival,
  } = useOdometerContext();

  // Odometer tracking is active if day is active
  const isOdometerTracking = dayData?.status === 'active';

  // Get the effective associate ID (self, or impersonated rep when active).
  const getAssociateId = useCallback((): number | null => getEffectiveAssociateId(), []);

  // Fetch route details
  useEffect(() => {
    async function fetchRoute() {
      if (!callId) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await routesApi.getRouteById(parseInt(callId));
        setRoute(data.route);
        // Write-through to the offline cache so a later signal loss can
        // still render this call (#1986 — kills diag #33 "Unable to load
        // call"). Best-effort; never block the happy path.
        void cacheRoute(parseInt(callId), data.route);
      } catch (err) {
        console.error('Failed to fetch route:', err);
        // Offline fallback (#1986): render the last-cached route instead
        // of dead-ending the rep. A 403/404 is authoritative, so only
        // fall back when there's no clear authz/not-found signal.
        const message = err instanceof Error ? err.message : 'Failed to load call details';
        const authoritative =
          message.includes('Not authorized') ||
          message.includes('403') ||
          message.includes('not found') ||
          message.includes('404');
        const cached = authoritative
          ? null
          : await getCachedRoute<Route>(parseInt(callId)).catch(() => null);
        if (cached) {
          setRoute(cached);
        } else if (message.includes('Not authorized') || message.includes('403')) {
          setError('You are not assigned to this call');
        } else if (message.includes('not found') || message.includes('404')) {
          setError('Call not found');
        } else {
          setError(message);
        }
      } finally {
        setIsLoading(false);
      }
    }

    fetchRoute();
  }, [callId]);

  // Handle GPS check-in completion
  // performCheckIn is recreated whenever dayData changes (it reads
  // dayData.status). The check-in handlers below are memoized and were NOT
  // listing performCheckIn as a dependency, so they froze around the FIRST
  // performCheckIn — the one captured while the day was still 'not_started'.
  // After the rep started their day, tapping check-in still ran that stale
  // closure, saw 'not_started', and bailed back to the Start-Day form —
  // an infinite Start-Day <-> Check-In loop that created NO CallLog (lost
  // check-in). Routing through a ref that always holds the latest
  // performCheckIn fixes it without making the handlers churn on every
  // dayData change. (#2005)
  const performCheckInRef =
    useRef<(gpsOverrideReason?: string, odometerReading?: number, dayAlreadyStarted?: boolean) => Promise<void>>();

  const handleGPSCheckInComplete = useCallback((gpsOverrideReason?: string) => {
    if (!route) return;

    // If the retroactive Start Day form already captured the arrival
    // odometer, don't prompt for it a second time (#1991 / diag #34
    // double odometer prompt) — go straight to check-in with it.
    if (retroactiveDriveData?.endOdometer != null) {
      void performCheckInRef.current?.(gpsOverrideReason, retroactiveDriveData.endOdometer);
      return;
    }

    // If odometer tracking is active, show odometer entry first
    if (isOdometerTracking) {
      setPendingGpsOverrideReason(gpsOverrideReason);
      setShowOdometerEntry(true);
      return;
    }

    // Otherwise, proceed directly with check-in
    void performCheckInRef.current?.(gpsOverrideReason);
  }, [route, isOdometerTracking, retroactiveDriveData]);

  // Handle odometer submission
  const handleOdometerSubmit = useCallback(async (reading: number) => {
    if (!route) return;

    setShowOdometerEntry(false);

    // Perform the check-in with the odometer reading
    await performCheckInRef.current?.(pendingGpsOverrideReason, reading);
  }, [route, pendingGpsOverrideReason]);

  // Perform the actual check-in
  const performCheckIn = useCallback(async (
    gpsOverrideReason?: string,
    odometerReading?: number,
    dayAlreadyStarted?: boolean,
  ) => {
    if (!route) return;

    setIsCheckingIn(true);
    setCheckInError(null);

    try {
      // Use legacystoreid for navigation and DayTracking (matches URL and call log filtering)
      // Use store_id (PK) only for API body where backend expects PK
      const legacyStoreId = Number(route.store.legacy_store_id) || route.store.store_id;
      const associateId = getAssociateId();

      if (!associateId) {
        setCheckInError('Unable to check in: Please log out and log back in.');
        setIsCheckingIn(false);
        return;
      }

      // Day must already be started by this point — the
      // retroactive Start Day form (showRetroactiveStartDay) blocks
      // check-in until the rep has supplied departure time + odos.
      // The silent startDay() that used to live here was the cause of
      // Rob's CallLog 955198 with DriveOdoStart=0 / DriveOdoEnd=1.
      // #2957 — `dayAlreadyStarted` is set by the auto-continue after the
      // retroactive backfill (handleRetroactiveStartDay). We JUST called
      // startDay(), so proceed unconditionally instead of re-reading
      // dayData.status: the status flip is async and a stale closure here
      // re-opened the backfill form, trapping the rep in the "didn't start day"
      // loop (diag #68/#71). Only gate the FIRST, rep-initiated tap.
      if (!dayAlreadyStarted && dayData.status === 'not_started') {
        // Remember any GPS override reason the rep already gave so the
        // auto-continued check-in (after the backfill) still carries it.
        setPendingGpsOverrideReason(gpsOverrideReason);
        setShowRetroactiveStartDay(true);
        setIsCheckingIn(false);
        return;
      }

      // Check into store via day tracking (local state) - use legacystoreid to match URL
      const closedDriveData = await checkIntoStore(legacyStoreId, route.store.store_name, route.project_id);
      // If a retroactive drive was captured via the form (rep forgot
      // Start Day), it takes precedence over whatever checkIntoStore
      // would have synthesized.
      // Guard against an inverted / zero-length drive segment — the
      // server rejects stdrivetime >= eddrivetime with "Start and End
      // Drive time not selected properly" (#1991 / diag #34). Dropping it
      // still checks the rep in via call time + odometers.
      // The outbound drive segment carries times but no odometer — fill the odo
      // pair from the start-day + check-in readings so the CallLog gets both
      // drive time and mileage. Both-or-neither: the server rejects a lone
      // driveodo, so a missing side yields a time-only drive.
      const startDayOdo = odometerData.readings?.find((r) => r.type === 'start_day')?.reading;
      const outStartOdo = closedDriveData?.startOdometer ?? startDayOdo;
      const outEndOdo = closedDriveData?.endOdometer ?? odometerReading;
      const bothOdo = outStartOdo != null && outEndOdo != null;
      const closedWithOdo = closedDriveData
        ? {
            ...closedDriveData,
            startOdometer: bothOdo ? outStartOdo : undefined,
            endOdometer: bothOdo ? outEndOdo : undefined,
          }
        : closedDriveData;
      const driveDataToUse = sanitizeDriveData(retroactiveDriveData || closedWithOdo);

      // Timestamps for the check-in payload.
      const todayStr = formatDateToLocal(new Date());
      const now = new Date();
      const timeStr = now.toTimeString().slice(0, 5);

      // Queue-only check-in (#1987 Phase 3 — the sync queue is the SOLE write
      // path). `checkInKey` is this visit's localId: checkInOp.applyLocal
      // writes the visit_task_sessions row keyed by it and enqueues the POST;
      // the sync engine retries on its own until the server CallLog lands, so
      // a dropped check-in (diag #33) is invisible — the rep proceeds
      // immediately on the localId. checkInOp.reconcile later stamps the
      // server calllogid back onto this same row, which the checkout/survey
      // ops resolve lazily via visitLocalId.
      const { enqueueOp } = await import('../sync/writeOps');
      await enqueueOp(
        'checkIn',
        {
          storeId: legacyStoreId,
          projectId: route.project_id,
          associateId,
          storeName: route.store.store_name,
          gps: dayData.currentLocation
            ? {
                lat: dayData.currentLocation.lat,
                lng: dayData.currentLocation.lng,
                accuracy: dayData.currentLocation.accuracy,
              }
            : undefined,
          odometer: odometerReading,
          // Drive-start odo for the odometer-only branch (no drive segment):
          // the running odometer before this reading, else the start-day
          // reading, else 0. The server requires both miles together.
          startOdometer:
            odometerData.currentReading ||
            odometerData.readings?.find((r) => r.type === 'start_day')?.reading ||
            0,
          dteCall: todayStr,
          stCallTime: `${todayStr}T${timeStr}:00`,
          gpsOverrideReason,
          driveData: driveDataToUse
            ? {
                startTime: driveDataToUse.startTime.toISOString(),
                endTime: driveDataToUse.endTime.toISOString(),
                startOdometer: driveDataToUse.startOdometer,
                endOdometer: driveDataToUse.endOdometer,
              }
            : undefined,
        },
        checkInKey,
      );

      // The localId we just enqueued under; hand it to StoreDetail below.
      const visitLocalId = checkInKey;
      // Rotate the idempotency key so a subsequent check-in attempt in the
      // same component lifetime gets a fresh localId.
      setCheckInKey(generateIdempotencyKey());

      // Navigate to StoreDetail on the visit localId. StoreDetail adopts this
      // exact session via ?visitId= so the active task shares the queue's id
      // (no server callLogId exists yet — it arrives when the queue syncs).
      const params = new URLSearchParams({
        projectId: route.project_id.toString(),
        routeId: route.route_id.toString(),
        taskType: 'call',
        projectDesc: route.project_description || '',
        clientName: route.client_name || '',
        visitId: visitLocalId,
      });
      navigate(`/store/${legacyStoreId}?${params.toString()}`);
    } catch (err) {
      console.error('Failed to check in:', err);
      // Inline, retryable — do NOT trip the fatal "Unable to load call"
      // screen (that's reserved for route-load failures). #1991
      setCheckInError(err instanceof Error ? err.message : 'Failed to check in');
      setIsCheckingIn(false);
    }
  }, [route, getAssociateId, dayData, startDay, checkIntoStore, setCurrentEntryCallLogId, odometerData, navigate, retroactiveDriveData]);

  // Always point the ref at the freshest performCheckIn so the memoized
  // check-in handlers above never invoke a stale 'not_started' closure (#2005).
  performCheckInRef.current = performCheckIn;

  // #2957 — once the retroactive Start-Day backfill has flipped the day to
  // 'active', auto-continue the check-in the rep already asked for. Gated on the
  // status flip (not fired immediately in handleRetroactiveStartDay) so
  // performCheckIn no longer sees 'not_started' — this removes both the required
  // second "Check In with GPS" tap and the re-prompt loop (diag #68). Uses the
  // ref so it always calls the freshest closure (with the buffered drive data).
  useEffect(() => {
    if (!autoCheckInPending) return;
    setAutoCheckInPending(false);
    // Fire on the pending flag ALONE — do NOT also gate on dayData.status ===
    // 'active'. handleRetroactiveStartDay only sets this flag AFTER awaiting
    // startDay(), so the day is already started (and persisted — a reload
    // proceeds fine). Gating on the reactive status flip is what left the rep
    // stuck: if the flip hadn't settled at eval time the check-in never ran and
    // the backfill form re-appeared (diag #71). Pass dayAlreadyStarted=true so
    // performCheckIn skips the start-day gate regardless of its status closure.
    void performCheckInRef.current?.(pendingGpsOverrideReason, undefined, true);
  }, [autoCheckInPending, pendingGpsOverrideReason]);

  // Open the retroactive Start Day form. Was a silent startDay() with
  // no data — caused Rob's CallLog 955198 with DriveOdoStart=0 /
  // DriveOdoEnd=1 because no start odo was ever recorded.
  const handleStartDayAndCheckIn = useCallback(() => {
    if (dayData.status === 'not_started') {
      setShowRetroactiveStartDay(true);
    }
  }, [dayData.status]);

  // Submitted from the inline RetroactiveStartDay form. Persists the
  // captured times + odometers into day-tracking + odometer state, and
  // buffers a driveDataToUse that the subsequent check-in will write
  // to the CallLog row.
  const handleRetroactiveStartDay = useCallback(async (data: RetroactiveStartData) => {
    if (data.startOdometer !== null) {
      await startDayOdometer(data.startOdometer, data.departureTime);
    }
    await startDay(data.departureTime);
    if (data.arrivalOdometer !== null && route) {
      const legacyStoreId = Number(route.store.legacy_store_id) || route.store.store_id;
      await recordStoreArrival(legacyStoreId, route.store.store_name ?? '', data.arrivalOdometer);
    }
    // #3019 — close the drive segment at the ACTUAL arrival time, not "now"
    // (the moment of the backfill), so a late check-in doesn't inflate the drive.
    const arrival = data.arrivalTime;
    const durationMinutes = Math.round((arrival.getTime() - data.departureTime.getTime()) / 60000);
    setRetroactiveDriveData({
      startTime: data.departureTime,
      endTime: arrival,
      durationMinutes,
      startOdometer: data.startOdometer ?? undefined,
      endOdometer: data.arrivalOdometer ?? undefined,
    });
    setShowRetroactiveStartDay(false);
    // #2957 — proceed into the task once the day flips to 'active' (see the effect
    // below), instead of dropping the rep back to tap Check In with GPS again.
    setAutoCheckInPending(true);
  }, [route, startDay, startDayOdometer, recordStoreArrival]);

  const handleSkipOdometerAtStartDay = useCallback(async () => {
    await startDay();
    setShowRetroactiveStartDay(false);
  }, [startDay]);

  const handleCancelStartDay = useCallback(() => {
    setShowRetroactiveStartDay(false);
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !route) {
    return (
      <div className="min-h-screen bg-slate-50 pb-24">
        <PageHeader title="Call Details" onBack={() => navigate(-1)} />
        <div className="p-4">
          <div className="rounded-xl bg-red-50 p-4 text-center text-red-700">
            <p className="font-medium">Unable to load call</p>
            <p className="mt-1 text-sm">{error || 'Call not found'}</p>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isComplete = route.visits_completed >= route.frequency;
  const visitsRemaining = route.frequency - route.visits_completed;
  const lastVisitFormatted = route.last_visit_date
    ? new Date(route.last_visit_date).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      })
    : 'Never';

  // Determine if we should show GPS check-in
  const showGPSCheckIn = !isComplete && !showOdometerEntry && !isCheckingIn && !showRetroactiveStartDay;
  const needsToStartDay = dayData.status === 'not_started';

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <PageHeader
        title={route.store.store_name}
        subtitle={`#${route.project_id} ${route.project_description}`}
        onBack={() => navigate(-1)}
      />

      <main className="p-4">
        {/* Status Banner */}
        <div
          className={`mb-4 rounded-xl p-4 ${
            isComplete
              ? 'bg-green-100 text-green-800'
              : visitsRemaining <= 1
              ? 'bg-amber-100 text-amber-800'
              : 'bg-cyan-100 text-cyan-800'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isComplete ? (
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                </svg>
              )}
              <div>
                <div className="font-semibold">
                  {isComplete ? 'Complete for this month' : `${visitsRemaining} visit${visitsRemaining !== 1 ? 's' : ''} remaining`}
                </div>
                <div className="text-sm opacity-90">
                  Last visit: {lastVisitFormatted}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">
                {route.visits_completed}/{route.frequency}
              </div>
            </div>
          </div>
          <ProgressBar completed={route.visits_completed} total={route.frequency} />
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 rounded-xl bg-red-50 p-4 text-center text-red-700">
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Retroactive Start Day — rep arrived at a call site without
            having clicked Start Day. Must collect departure time +
            start odo + arrival odo before any check-in. */}
        {showRetroactiveStartDay && (
          <div className="mb-4">
            <RetroactiveStartDay
              storeName={route.store.store_name ?? 'this store'}
              onSubmit={handleRetroactiveStartDay}
              onCancel={handleCancelStartDay}
              onSkip={handleSkipOdometerAtStartDay}
              isLoading={isCheckingIn}
            />
          </div>
        )}

        {/* Odometer Entry */}
        {showOdometerEntry && (
          <div className="mb-4">
            <OdometerEntry
              type="store_arrival"
              onSubmit={handleOdometerSubmit}
              onCancel={() => setShowOdometerEntry(false)}
              onSkip={() => {
                setShowOdometerEntry(false);
                performCheckIn(pendingGpsOverrideReason);
              }}
              isLoading={isCheckingIn}
            />
          </div>
        )}

        {/* Check-in error (inline, retryable — not the fatal load screen). #1991 */}
        {checkInError && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="font-medium text-red-700">Check-in didn't go through</p>
            <p className="mt-1 text-sm text-red-600">{checkInError}</p>
            <p className="mt-2 text-xs text-red-500">Your info is still here — tap check-in to try again.</p>
          </div>
        )}

        {/* GPS Check-In Section */}
        {showGPSCheckIn && (
          <div className="mb-4">
            {needsToStartDay ? (
              <button
                onClick={handleStartDayAndCheckIn}
                className="w-full rounded-xl bg-primary-600 py-4 text-center font-semibold text-white shadow-lg transition-all hover:bg-primary-700 active:scale-[0.98]"
              >
                <span className="flex items-center justify-center gap-2">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Clock In
                </span>
              </button>
            ) : route.store.lat && route.store.lon ? (
              <GPSCheckIn
                storeLat={route.store.lat}
                storeLng={route.store.lon}
                storeName={route.store.store_name ?? ''}
                maxDistanceMeters={200}
                onCheckIn={handleGPSCheckInComplete}
                isLoading={isCheckingIn}
              />
            ) : (
              <div className="text-center text-slate-500 py-4">
                Store location not available for GPS check-in
              </div>
            )}
          </div>
        )}

        {/* Checking In Indicator */}
        {isCheckingIn && (
          <div className="mb-4 flex items-center justify-center gap-3 rounded-xl bg-green-50 p-4">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-green-600 border-t-transparent"></div>
            <span className="font-medium text-green-700">Checking in...</span>
          </div>
        )}

        {/* Store Information */}
        <Section title="Store">
          <InfoRow label="Store #" value={route.store.legacy_store_id || route.store.store_number} />
          <InfoRow label="Address" value={route.store.address} />
          <InfoRow label="City" value={`${route.store.city}, ${route.store.state} ${route.store.zip_code}`} />
          <InfoRow label="Retailer" value={route.store.retailer_name} />
        </Section>

        {/* Call Details */}
        <Section title="Call Details">
          <InfoRow label="Project" value={route.project_description} />
          {route.client_name && <InfoRow label="Client" value={route.client_name} />}
          <InfoRow label="Frequency" value={`${route.frequency}x per month`} />
          {route.hours_per_visit && <InfoRow label="Est. Hours" value={`${route.hours_per_visit}h per visit`} />}
          <InfoRow label="Visits Completed" value={`${route.visits_completed} of ${route.frequency}`} />
          <InfoRow label="Last Visit" value={lastVisitFormatted} />
        </Section>

        {/* Get Directions Button */}
        {!isComplete && (
          <div className="mt-6">
            <button
              onClick={() => {
                // Open maps for directions
                const address = encodeURIComponent(
                  `${route.store.address}, ${route.store.city}, ${route.store.state} ${route.store.zip_code}`
                );
                window.open(`https://maps.google.com/maps?q=${address}`, '_blank');
              }}
              className="w-full rounded-xl border border-slate-300 bg-white py-4 text-center font-semibold text-slate-700 transition-all hover:bg-slate-50 active:scale-[0.98]"
            >
              Get Directions
            </button>
          </div>
        )}

        {/* Complete Message */}
        {isComplete && (
          <div className="mt-6 rounded-xl bg-green-50 p-4 text-center">
            <svg className="mx-auto h-12 w-12 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <p className="mt-2 font-semibold text-green-800">All visits completed for this month!</p>
            <p className="mt-1 text-sm text-green-600">
              Great job! You've completed all {route.frequency} required visits.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
